import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, Bot, User, Loader2 } from 'lucide-react';
import { supabase } from '../config/supabase';
import { useLanguage } from '../contexts/LanguageProvider';

export default function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { language } = useLanguage();

  // Translations
  const translations = {
    en: {
      title: "Local Area Assistant",
      subtitle: "Ask me about Ano Viannos, beaches, restaurants & activities!",
      placeholder: "Ask about local beaches, restaurants, activities...",
      send: "Send",
      exampleQuestions: [
        "What are the best beaches nearby?",
        "Recommend restaurants for dinner",
        "What activities can we do if it rains?",
        "How far is Heraklion airport?"
      ],
      tryAsking: "Try asking:",
      welcomeMessage: "Hi! I'm here to help you discover the best of Ano Viannos! Ask me about beaches, restaurants, activities, or anything local. 🏖️",
      errorMessage: "Sorry, I couldn't process that request. Please try again.",
      rateLimitMessage: "⏱️ Too many questions! Please wait a moment before asking another question.",
      closeChat: "Close chat",
      sendMessage: "Send message"
    },
    gr: {
      title: "Τοπικός Βοηθός",
      subtitle: "Ρωτήστε με για το Άνω Βιάννο, παραλίες, εστιατόρια και δραστηριότητες!",
      placeholder: "Ρωτήστε για παραλίες, εστιατόρια, δραστηριότητες...",
      send: "Αποστολή",
      exampleQuestions: [
        "Ποιες είναι οι καλύτερες παραλίες εδώ κοντά;",
        "Προτείνετε εστιατόρια για δείπνο",
        "Τι δραστηριότητες μπορούμε να κάνουμε αν βρέχει;",
        "Πόσο μακριά είναι το αεροδρόμιο Ηρακλείου;"
      ],
      tryAsking: "Δοκιμάστε να ρωτήσετε:",
      welcomeMessage: "Γεια σας! Είμαι εδώ για να σας βοηθήσω να ανακαλύψετε το καλύτερο του Άνω Βιάννου! Ρωτήστε με για παραλίες, εστιατόρια, δραστηριότητες! 🏖️",
      errorMessage: "Συγγνώμη, δεν μπόρεσα να επεξεργαστώ αυτό το αίτημα. Παρακαλώ δοκιμάστε ξανά.",
      rateLimitMessage: "⏱️ Πάρα πολλά αιτήματα! Παρακαλώ περιμένετε λίγο πριν κάνετε άλλη ερώτηση.",
      closeChat: "Κλείσιμο συνομιλίας",
      sendMessage: "Αποστολή μηνύματος"
    }
  };

  const t = (key) => translations[language]?.[key] || translations.en[key];

  // Initialize with welcome message and update when language changes
  useEffect(() => {
    const welcomeMessage = {
      id: Date.now(),
      type: 'ai',
      content: t('welcomeMessage'),
      timestamp: new Date()
    };
    
    if (messages.length === 0) {
      // First time - add welcome message
      setMessages([welcomeMessage]);
    } else {
      // Language changed - update the first message if it's a welcome message
      setMessages(prev => {
        const updatedMessages = [...prev];
        if (updatedMessages[0]?.type === 'ai' && updatedMessages.length > 0) {
          // Update the first AI message (welcome message) with new language
          updatedMessages[0] = {
            ...updatedMessages[0],
            content: t('welcomeMessage'),
            timestamp: new Date()
          };
        }
        return updatedMessages;
      });
    }
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleExampleClick = (question) => {
    setInputMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputMessage.trim() && !isLoading) {
        sendMessage();
      }
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: { 
          message: userMessage.content,
          conversationHistory: messages.slice(-10) // Send last 10 messages for context
        }
      });

      if (error) {
        throw error;
      }

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMsg = t('errorMessage');
      
      // Handle rate limiting
      if (error.message?.includes('rate limit') || error.message?.includes('Rate limit')) {
        errorMsg = t('rateLimitMessage');
      }
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: errorMsg,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const chatWindowStyle = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '350px',
    height: '500px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
    border: '1px solid #ddd',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    fontFamily: 'system-ui, sans-serif'
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          aria-label={t('title')}
          title={t('subtitle')}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#2563eb',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <MessageCircle size={24} aria-hidden="true" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div style={chatWindowStyle}>
          {/* Header */}
          <div style={{
            backgroundColor: '#2563eb',
            color: 'white',
            padding: '15px',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bot size={20} />
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{t('title')}</div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.95)' }}>{t('subtitle')}</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              aria-label={t('closeChat')}
              title={t('closeChat')}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            padding: '15px',
            overflowY: 'auto',
            backgroundColor: '#f8f9fa'
          }}>
            {messages.map((message) => (
              <div key={message.id} style={{
                display: 'flex',
                marginBottom: '15px',
                flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: message.type === 'user' ? '#6b7280' : '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {message.type === 'user' ? 
                    <User size={14} color="white" /> : 
                    <Bot size={14} color="white" />
                  }
                </div>
                <div style={{
                  maxWidth: '70%',
                  padding: '10px 12px',
                  borderRadius: '12px',
                  backgroundColor: message.type === 'user' ? '#2563eb' : 'white',
                  color: message.type === 'user' ? 'white' : 'black',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  border: message.type === 'ai' ? '1px solid #e5e7eb' : 'none'
                }}>
                  {message.content}
                  <div style={{
                    fontSize: '11px',
                    marginTop: '5px',
                    opacity: 0.7
                  }}>
                    {formatTime(message.timestamp)}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '15px'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bot size={14} color="white" />
                </div>
                <div style={{
                  padding: '10px 12px',
                  borderRadius: '12px',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Loader2 size={14} style={{
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{ fontSize: '14px' }}>Thinking...</span>
                </div>
              </div>
            )}

            {/* Example Questions */}
            {messages.length === 1 && (
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '10px'
              }}>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: 'bold', 
                  marginBottom: '8px',
                  color: '#374151'
                }}>
                  {t('tryAsking')}
                </div>
                {t('exampleQuestions').map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleClick(question)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '6px 8px',
                      margin: '2px 0',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#2563eb',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#eff6ff'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    "{question}"
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={{
            padding: '15px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: 'white',
            borderRadius: '0 0 12px 12px'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <label htmlFor="ai-chat-input" style={{ display: 'none' }}>
                {t('placeholder')}
              </label>
              <input
                id="ai-chat-input"
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={t('placeholder')}
                disabled={isLoading}
                aria-label={t('placeholder')}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: isLoading ? '#f9fafb' : 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                aria-label={t('sendMessage')}
                title={t('sendMessage')}
                style={{
                  padding: '10px 12px',
                  backgroundColor: (!inputMessage.trim() || isLoading) ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!inputMessage.trim() || isLoading) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Send size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `
      }} />
    </>
  );
}
